import { useEffect, useRef } from 'react';

interface UseAnchorScrollOptions {
  /**
   * Maximum number of attempts to find and scroll to the element
   * @default 50
   */
  maxAttempts?: number;
  /**
   * Delay between attempts in milliseconds
   * @default 100
   */
  attemptDelay?: number;
  /**
   * Scroll behavior
   * @default 'smooth'
   */
  behavior?: ScrollBehavior;
  /**
   * Block alignment
   * @default 'start'
   */
  block?: ScrollLogicalPosition;
}

export const useAnchorScroll = (options: UseAnchorScrollOptions = {}) => {
  const {
    maxAttempts = 50,
    attemptDelay = 100,
    behavior = 'smooth',
    block = 'start'
  } = options;

  const executedScrolls = useRef(new Set<string>());

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const elementId = hash.substring(1);

    // Skip if we've already scrolled to this anchor in this session
    if (executedScrolls.current.has(elementId)) {
      return;
    }

    let attempts = 0;

    const tryScroll = () => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior, block });
        // Mark this anchor as executed
        executedScrolls.current.add(elementId);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScroll, attemptDelay);
      }
    };

    tryScroll();
  }, [behavior, block, maxAttempts, attemptDelay]);
};
